import { useMemo, useState } from 'react';
import { mockData, ABTest, ChurnTrigger, ModelRoutingRule, PriceAdjustment } from '../api/client';

type Tab = 'ab-tests' | 'pricing' | 'churn' | 'routing';

export default function Optimization() {
  const [activeTab, setActiveTab] = useState<Tab>('ab-tests');
  const abTests = useMemo(() => mockData.abTests(), []);
  const priceAdjustments = useMemo(() => mockData.priceAdjustments(), []);
  const churnTriggers = useMemo(() => mockData.churnTriggers(), []);
  const routingRules = useMemo(() => mockData.modelRoutingRules(), []);

  const tabs = [
    { id: 'ab-tests' as Tab, name: 'A/B Tests', count: abTests.filter((t) => t.status === 'running').length },
    { id: 'pricing' as Tab, name: 'Pricing', count: priceAdjustments.filter((p) => p.status === 'pending').length },
    { id: 'churn' as Tab, name: 'Churn Triggers', count: churnTriggers.filter((t) => t.enabled).length },
    { id: 'routing' as Tab, name: 'Model Routing', count: routingRules.filter((r) => r.enabled).length },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dynamic Optimization</h1>
        <p className="text-slate-400 mt-1">Manage experiments, pricing, and optimization rules</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-primary-500/20 text-primary-400' : 'bg-slate-700 text-slate-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'ab-tests' && <ABTestsTab tests={abTests} />}
      {activeTab === 'pricing' && <PricingTab adjustments={priceAdjustments} />}
      {activeTab === 'churn' && <ChurnTriggersTab triggers={churnTriggers} />}
      {activeTab === 'routing' && <ModelRoutingTab rules={routingRules} />}
    </div>
  );
}

function ABTestsTab({ tests }: { tests: ABTest[] }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-500/20 text-green-400';
      case 'completed':
        return 'bg-slate-600 text-slate-300';
      case 'draft':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-slate-600 text-slate-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Active Experiments</h2>
        <button className="btn-primary">Create New Test</button>
      </div>

      <div className="space-y-4">
        {tests.map((test) => (
          <div key={test.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-white">{test.name}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(test.status)}`}>
                    {test.status}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1">
                  Target: {test.targetMetric.replace(/_/g, ' ')}
                  {test.startDate && ` | Started: ${new Date(test.startDate).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex gap-2">
                {test.status === 'running' && (
                  <>
                    <button className="btn-secondary text-sm px-3 py-1.5">Pause</button>
                    <button className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg hover:bg-green-500/30 transition-colors font-medium text-sm">
                      Declare Winner
                    </button>
                  </>
                )}
                {test.status === 'draft' && (
                  <button className="btn-primary text-sm px-3 py-1.5">Start Test</button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {test.variants.map((variant) => {
                const isWinning = test.status === 'running' &&
                  variant.conversionRate === Math.max(...test.variants.map((v) => v.conversionRate));
                return (
                  <div
                    key={variant.id}
                    className={`p-4 rounded-lg border ${
                      isWinning ? 'border-green-500/30 bg-green-500/10' : 'border-slate-700 bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{variant.name}</span>
                      {isWinning && (
                        <span className="text-xs text-green-400 font-medium">Leading</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Traffic</span>
                        <span className="text-white">{variant.traffic}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Conversions</span>
                        <span className="text-white">{variant.conversions.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Rate</span>
                        <span className={`font-medium ${isWinning ? 'text-green-400' : 'text-white'}`}>
                          {variant.conversionRate}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingTab({ adjustments }: { adjustments: PriceAdjustment[] }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Suggested Price Adjustments</h2>
        <button className="btn-primary">Run Price Analysis</button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Tier</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Current Price</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Suggested Price</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Change</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Reason</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {adjustments.map((adj) => {
              const change = ((adj.suggestedPrice - adj.currentPrice) / adj.currentPrice) * 100;
              return (
                <tr key={adj.id} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-white capitalize">{adj.tier}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    ${adj.currentPrice}/mo
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-white">
                    ${adj.suggestedPrice}/mo
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-green-400 font-medium">+{change.toFixed(1)}%</span>
                  </td>
                  <td className="px-6 py-4 max-w-xs">
                    <p className="text-sm text-slate-400 truncate" title={adj.reason}>
                      {adj.reason}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg hover:bg-green-500/30 transition-colors font-medium text-sm">
                        Approve
                      </button>
                      <button className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500/30 transition-colors font-medium text-sm">
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Price Sensitivity Analysis</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Price Elasticity</p>
            <p className="text-2xl font-bold text-white mt-1">-0.42</p>
            <p className="text-sm text-slate-400 mt-1">1% price increase = 0.42% demand decrease</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Optimal Price Point</p>
            <p className="text-2xl font-bold text-white mt-1">$67/mo</p>
            <p className="text-sm text-slate-400 mt-1">Maximizes revenue for Pro tier</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Revenue Impact</p>
            <p className="text-2xl font-bold text-green-400 mt-1">+$18,500/mo</p>
            <p className="text-sm text-slate-400 mt-1">If all suggestions approved</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChurnTriggersTab({ triggers }: { triggers: ChurnTrigger[] }) {
  const [localTriggers, setLocalTriggers] = useState(triggers);

  const toggleTrigger = (id: string) => {
    setLocalTriggers((prev) =>
      prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t))
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Churn Prevention Triggers</h2>
        <button className="btn-primary">Add New Trigger</button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Condition</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Action</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {localTriggers.map((trigger) => (
              <tr key={trigger.id} className="hover:bg-slate-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-medium text-white">{trigger.name}</span>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm bg-slate-700 px-2 py-1 rounded text-slate-300">
                    {trigger.condition}
                  </code>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-300">{trigger.action.replace(/_/g, ' ')}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleTrigger(trigger.id)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      trigger.enabled ? 'bg-primary-600' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        trigger.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button className="text-primary-400 hover:text-primary-300 font-medium text-sm">
                      Edit
                    </button>
                    <button className="text-red-400 hover:text-red-300 font-medium text-sm">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="card">
          <p className="text-sm text-slate-400">Triggered This Week</p>
          <p className="text-3xl font-bold text-white mt-2">247</p>
          <p className="text-sm text-green-400 mt-1">+12% from last week</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-400">Churn Prevented</p>
          <p className="text-3xl font-bold text-green-400 mt-2">34</p>
          <p className="text-sm text-slate-400 mt-1">Users retained after intervention</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-400">Revenue Saved</p>
          <p className="text-3xl font-bold text-white mt-2">$4,230</p>
          <p className="text-sm text-slate-400 mt-1">MRR preserved</p>
        </div>
      </div>
    </div>
  );
}

function ModelRoutingTab({ rules }: { rules: ModelRoutingRule[] }) {
  const [localRules, setLocalRules] = useState(rules);

  const toggleRule = (id: string) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Model Routing Rules</h2>
        <button className="btn-primary">Add New Rule</button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Condition</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Target Model</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {localRules.sort((a, b) => a.priority - b.priority).map((rule) => (
              <tr key={rule.id} className="hover:bg-slate-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-600 text-slate-300">
                    {rule.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-medium text-white">{rule.name}</span>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm bg-slate-700 px-2 py-1 rounded text-slate-300">
                    {rule.condition}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                    {rule.targetModel}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleRule(rule.id)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      rule.enabled ? 'bg-primary-600' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        rule.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button className="text-primary-400 hover:text-primary-300 font-medium text-sm">
                      Edit
                    </button>
                    <button className="text-red-400 hover:text-red-300 font-medium text-sm">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Routing Performance</h3>
        <div className="grid grid-cols-4 gap-6">
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Total Requests Routed</p>
            <p className="text-2xl font-bold text-white mt-1">1.2M</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Avg Latency</p>
            <p className="text-2xl font-bold text-white mt-1">142ms</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Cost Savings</p>
            <p className="text-2xl font-bold text-green-400 mt-1">$8,450</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Quality Score</p>
            <p className="text-2xl font-bold text-white mt-1">94.2%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
